import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Eye, EyeOff, Mail, ArrowLeft } from 'lucide-react';
import { UdvegaLogo } from '../components/UdvegaLogo';
import { track, identifyUser } from '../lib/posthog';

const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };

export default function Login() {
  const { login, register, resendVerification } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const openAsRegister = pathname.startsWith('/register') || searchParams.get('signup') === '1';
  const [isRegister, setIsRegister] = useState(openAsRegister);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', email: '', password: '', company: '' });
  const [emailSent, setEmailSent] = useState(false);
  const [sentEmail, setSentEmail] = useState('');
  const [resending, setResending] = useState(false);

  useEffect(() => {
    setIsRegister(pathname.startsWith('/register') || searchParams.get('signup') === '1');
  }, [pathname, searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (isRegister) {
      if (!form.name || !form.email || !form.password) { setError('All fields are required.'); setLoading(false); return; }
      const result = await register(form.name, form.email, form.password, form.company);
      setLoading(false);
      if (result.requires_verification) {
        setSentEmail(result.email);
        setEmailSent(true);
        return;
      }
      if (result.success) { navigate('/dashboard'); return; }
      setError(result.error);
    } else {
      if (!form.email || !form.password) { setError('Email and password are required.'); setLoading(false); return; }
      track('user_login_attempt', { email_domain: form.email.split('@')[1] || '' });
      const result = await login(form.email, form.password);
      setLoading(false);
      if (result.success) {
        track('user_login_success');
        identifyUser({ email: form.email });
        navigate('/dashboard');
        return;
      }
      track('user_login_failed');
      setError(result.error);
    }
  };

  const handleResend = async () => {
    setResending(true);
    await resendVerification(sentEmail);
    setResending(false);
  };

  // "Check your email" screen after registration
  if (emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16 pb-16 px-4" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #134E4A 100%)' }} data-testid="verify-email-sent-page">
        <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ duration: 0.4 }} className="w-full max-w-md">
          <div className="glass-card rounded-3xl p-8 md:p-10 bg-white/80 text-center">
            <div className="w-16 h-16 rounded-2xl bg-teal-50 flex items-center justify-center mx-auto mb-6">
              <Mail className="w-8 h-8 text-teal-600" />
            </div>
            <h2 className="font-heading text-2xl font-medium text-slate-800 mb-2">Check Your Email</h2>
            <p className="text-sm text-slate-500 mb-1">We sent a verification link to</p>
            <p className="text-sm font-semibold text-teal-600 mb-6">{sentEmail}</p>
            <p className="text-sm text-slate-400 leading-relaxed mb-8">
              Click the link in the email to verify your account. The link expires in 24 hours.
            </p>
            <Button
              onClick={handleResend}
              disabled={resending}
              variant="outline"
              className="w-full border-slate-300 mb-3"
              data-testid="resend-verification-btn"
            >
              {resending ? 'Sending...' : "Didn't get it? Resend verification link"}
            </Button>
            <button
              onClick={() => { setEmailSent(false); setError(''); }}
              className="flex items-center justify-center gap-1 text-xs text-slate-400 hover:text-slate-600 mx-auto mt-3"
            >
              <ArrowLeft className="w-3 h-3" /> Back to registration
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center pt-16 pb-16 px-4" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #134E4A 100%)' }} data-testid="login-page">
      <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ duration: 0.4 }} className="w-full max-w-md">
        <div className="glass-card rounded-3xl p-8 md:p-10 bg-white/80">
          <div className="flex justify-center mb-8">
            <UdvegaLogo size="lg" />
          </div>
          <h2 className="font-heading text-2xl font-medium text-slate-800 text-center mb-1">
            {isRegister ? 'Create Account' : 'Welcome Back'}
          </h2>
          <p className="text-sm text-slate-500 text-center mb-8">
            {isRegister ? 'Start protecting your workforce today' : 'Sign in to your HR dashboard'}
          </p>
          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Full Name</label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Smith" className="border-slate-200 focus:ring-teal-500/30 focus:border-teal-500" data-testid="register-name-input" />
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Email</label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@company.com" className="border-slate-200 focus:ring-teal-500/30 focus:border-teal-500" data-testid="login-email-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Password</label>
              <div className="relative">
                <Input type={showPassword ? 'text' : 'password'} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Enter your password" className="border-slate-200 focus:ring-teal-500/30 focus:border-teal-500 pr-10" data-testid="login-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="toggle-password-visibility">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {!isRegister && (
                <div className="mt-2 text-right">
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/accounts/password_reset`}
                    className="text-sm text-teal-600 hover:text-teal-700 font-medium"
                    data-testid="forgot-password-link"
                  >
                    Forgot Password?
                  </a>
                </div>
              )}
            </div>
            {isRegister && (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Company (Optional)</label>
                <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Acme Corp" className="border-slate-200" data-testid="register-company-input" />
              </div>
            )}
            {error && <p className="text-sm text-red-500" data-testid="auth-error-msg">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full bg-teal-600 hover:bg-teal-700 text-white h-11" data-testid="auth-submit-btn">
              {loading ? 'Please wait...' : (isRegister ? 'Create Account' : 'Sign In')}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <button onClick={() => { setIsRegister(!isRegister); setError(''); }} className="text-sm text-teal-600 hover:text-teal-700 font-medium" data-testid="toggle-auth-mode">
              {isRegister ? 'Already have an account? Sign in' : "Don't have an account? Register"}
            </button>
          </div>
          <div className="mt-4 text-center">
            <Link to="/" className="text-xs text-slate-400 hover:text-slate-600">Back to Home</Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
